import React, { useState, useEffect, useContext } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Card, Tabs } from "@shopify/polaris";
import { PrivateContext } from "lib/context";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import lib
import { baseHelper, errorHelper } from "lib/helpers";
import { Banner, ResourceList } from "lib/components";
import constant from "lib/constant/constant";

// import gql
import { GET_ACTIVITY } from "app/userManagement/apollo/queries/index";

import ActivityListView from "./activityListView";
import ActivityListFooter from "./activityListPagination";

const ActivityList = () => {
  const { history, cms } = useContext(PrivateContext);
  const { gql } = constant;
  const prevFilter = baseHelper.queryParamsFromLocation(history);
  const [selected, setSelected] = useState(parseInt(prevFilter.tab, 10) || 0);
  const [selectedLimit, setSelectedLimit] = useState(parseInt(prevFilter.perPage, 10) || 10);
  const [currentPage, setCurrentPage] = useState(parseInt(prevFilter.page, 10) || 1);
  const [bulkLog, setBulkLog] = useState(false);
  const [queryValue, setQueryValue] = useState(prevFilter.search || null);
  const selectedSort = prevFilter.sort_name && `${prevFilter.sort_name}_${prevFilter.sort_order}`;
  const [sortValue, setSortValue] = useState(selectedSort || constant.CREATED_DESC);
  const [totalLog, setTotalLog] = useState(0);
  const [dataToFetch, setDataToFetch] = useState({
    search: prevFilter.search,
    filter: prevFilter.filter,
    perPage: selectedLimit,
    page: currentPage,
    sort_name: prevFilter.sort_name || constant.CREATED_AT,
    sort_order: prevFilter.sort_order || constant.DESC,
  });
  const [banner, setBanner] = useState({
    isOpen: false,
    title: "",
    message: "",
    status: "",
  });

  const inputData = {
    search: dataToFetch.search,
    filter: dataToFetch.filter,
    page: parseInt(dataToFetch.page, 10),
    perPage: parseInt(dataToFetch.perPage, 10),
    sort_name: dataToFetch.sort_name,
    sort_order: dataToFetch.sort_order,
  };

  const { data: activityData, loading: activityLoading, error: activityError } = useQuery(GET_ACTIVITY, {
    variables: {
      input: inputData,
    },
  });

  useEffect(() => {
    if (activityError) {
      setBanner({ status: constant.CRITICAL, title: errorHelper.parse(activityError), isOpen: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityError, errorHelper]);

  useEffect(() => {
    const responseData = baseHelper.getResponseData(activityData, gql.GET_ACTIVITY);
    const responseError = baseHelper.getResponseError(activityData, gql.GET_ACTIVITY);
    if (responseData) {
      const { count: totalCount = 0 } = responseData;
      setBulkLog(responseData.activity);
      setTotalLog(totalCount);
    }
    if (responseError) {
      setBanner({ status: constant.CRITICAL, title: responseError, isOpen: true });
    }
  }, [activityData, gql.GET_ACTIVITY]);

  const renderItem = (item, _, keyIndex) => {
    return <ActivityListView item={item} keyIndex={keyIndex} cms={cms} />;
  };

  const tabs = [
    {
      id: "allActivity",
      content: cms("label.tab.all"),
      accessibilityLabel: cms("label.tab.all"),
      panelID: "allActivity",
      value: "all",
    },
    {
      id: "orderActivity",
      content: cms("label.tab.order"),
      accessibilityLabel: cms("label.tab.order"),
      panelID: "orderActivity",
      value: "order",
    },
    {
      id: "productActivity",
      content: cms("label.tab.product"),
      accessibilityLabel: cms("label.tab.product"),
      panelID: "productActivity",
      value: "product",
    },
  ];

  const handlePerPage = (page) => {
    setCurrentPage(parseInt(page, 10));
    baseHelper.setUrl(history, { page });
    const filteredObj = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filteredObj });
  };

  const handleQueryValueChange = (value) => {
    baseHelper.setUrl(history, { search: value });
    setQueryValue(value);
    const filteredObj = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filteredObj });
  };

  const handleTabChange = (selectedTabIndex) => {
    const { value: tabName } = tabs[selectedTabIndex];
    setSelected(selectedTabIndex);
    setCurrentPage(1);
    baseHelper.setUrl(history, { tab: selectedTabIndex, filter: tabName, page: 1 });
    const filteredObj = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filteredObj });
  };

  const handleSearchTextRemove = () => {
    setQueryValue(null);
    baseHelper.setUrl(history, { search: "" });
    const filteredObj = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filteredObj });
  };

  const handleClearAll = () => {
    handleSearchTextRemove();
  };

  const handleQueryValueRemove = () => {
    baseHelper.setUrl(history, { search: "" });
    setQueryValue("");
    const filteredObj = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filteredObj });
  };

  const sortFilter = (selectedItem) => {
    const sort = selectedItem.split("_");
    if (sort && sort.length === 2) {
      baseHelper.setUrl(history, { sort_name: sort[0], sort_order: sort[1] });
      setSortValue(selectedItem);
      const filteredObj = baseHelper.queryParamsFromLocation(history);
      setDataToFetch({ ...dataToFetch, ...filteredObj });
    }
  };

  const handleSelectLimit = (value) => {
    setSelectedLimit(parseInt(value, 10));
    setCurrentPage(1);
    baseHelper.setUrl(history, { perPage: parseInt(value, 10), page: 1 });
    const result = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...result });
  };

  const sortOptions = [
    { label: cms("common.label.sortOption.dateAsc"), value: constant.CREATED_ASC },
    { label: cms("common.label.sortOption.dateDesc"), value: constant.CREATED_DESC },
  ];

  const limitOptions = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
  ];

  return (
    <>
      {banner.isOpen && (
        <>
          <Banner
            status={banner.status}
            title={banner.title}
            isOpen={banner.isOpen}
            onDismiss={() => {
              setBanner({ status: "", title: "", isOpen: false });
            }}
          />
          <br />
        </>
      )}
      <Card>
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
          <ResourceList
            resourceName={cms("label.resourceName")}
            items={bulkLog || []}
            renderItem={renderItem}
            onQueryChange={handleQueryValueChange}
            onQueryClear={handleQueryValueRemove}
            handleClearAll={handleClearAll}
            queryValue={queryValue}
            sortValue={sortValue}
            sortOptions={sortOptions}
            onSortChange={sortFilter}
            loading={activityLoading}
          />
        </Tabs>
        <ActivityListFooter
          optionsList={limitOptions}
          onSelectLimit={handleSelectLimit}
          perPageCount={selectedLimit}
          totalCount={totalLog}
          onPerPage={handlePerPage}
          currentPage={currentPage}
          cms={cms}
        />
      </Card>
    </>
  );
};

export default withFeature(withErrorBoundary(ActivityList), { feature: constant.ACTIVITY_LIST });
